// ============================================================
// ICP Qualifier Wizard
// Step-by-step ICP qualification flow that runs BEFORE a lead
// enters the CRM. Uses Hrishi's scoring engine.
// ============================================================
import React, { useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, AlertTriangle, Sparkles, Target, IndianRupee, Users, MapPin } from 'lucide-react';
import {
  calculateCompositeScore,
  createDefaultConfig,
  FIELD_OPTIONS,
  type Lead,
  type ScoreResult,
  type YesPartialNo,
  type DMRole,
  type Timeline,
  type LeadSource,
} from '../lib/lead-scoring';

// ── Shared lookup types ─────────────────────────────────────
interface LookupItem { id?: number | string; name: string; isActive?: boolean }

interface ICPWizardProps {
  onClose: () => void;
  onComplete: (payload: {
    // Fields to prefill the existing LeadFormModal
    companyName: string;
    contactPerson: string;
    email: string;
    designation: string;
    geography: string;
    industry: string;
    leadSource: string;
    estimatedRevenue: string;
    budget: string;
    comments: string;           // ICP summary block goes here
    painPoints: string;         // captured notes
    priority: 'High' | 'Medium' | 'Low';
    // Score metadata (parent can use to tag the lead)
    scores: ScoreResult;
  }) => void;
  industries?: LookupItem[];
  geographies?: LookupItem[];
  leadSources?: LookupItem[];
}

// ── Helpers ─────────────────────────────────────────────────
const tierColors: Record<string, { bg: string; text: string; ring: string; label: string; desc: string }> = {
  '1': { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', label: 'PURSUE', desc: 'Strong ICP fit — prioritise this lead' },
  '2': { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200',    label: 'PIPELINE', desc: 'Good fit — add to active pipeline' },
  '3': { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   label: 'NURTURE', desc: 'Partial fit — nurture over time' },
  '4': { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200',    label: 'DEPRIORITIZE', desc: 'Weak fit — deprioritize or disqualify' },
};

const tierPriority = (tier: string): 'High' | 'Medium' | 'Low' =>
  tier === '1' ? 'High' : tier === '2' ? 'Medium' : 'Low';

// Small reusable bits ─────────────────────────────────────────
const ChoiceButton = ({ active, onClick, children, tone = 'default' }: any) => {
  const toneMap: Record<string, string> = {
    yes:     active ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 hover:border-emerald-300 text-gray-700',
    partial: active ? 'border-amber-500 bg-amber-50 text-amber-800'       : 'border-gray-200 hover:border-amber-300 text-gray-700',
    no:      active ? 'border-rose-500 bg-rose-50 text-rose-800'          : 'border-gray-200 hover:border-rose-300 text-gray-700',
    default: active ? 'border-pierian bg-pierian/10 text-pierian'         : 'border-gray-200 hover:border-pierian/50 text-gray-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-md border-2 text-sm font-medium transition-all ${toneMap[tone]}`}
    >
      {children}
    </button>
  );
};

const YPNButtons = ({ value, onChange }: { value: YesPartialNo; onChange: (v: YesPartialNo) => void }) => (
  <div className="grid grid-cols-3 gap-2">
    <ChoiceButton tone="yes"     active={value === 'Yes'} onClick={() => onChange('Yes')}>
      <span className="flex items-center gap-1.5"><Check size={14}/> Yes</span>
    </ChoiceButton>
    <ChoiceButton tone="partial" active={value === 'Partially or through a partner'} onClick={() => onChange('Partially or through a partner')}>
      Partial / Partner
    </ChoiceButton>
    <ChoiceButton tone="no"      active={value === 'No'} onClick={() => onChange('No')}>
      <span className="flex items-center gap-1.5"><X size={14}/> No</span>
    </ChoiceButton>
  </div>
);

const FieldLabel = ({ children, hint }: { children: React.ReactNode; hint?: string }) => (
  <div className="mb-1.5">
    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">{children}</label>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const StepPill = ({ num, label, active, done }: { num: number; label: string; active: boolean; done: boolean }) => (
  <div className="flex items-center gap-2 flex-1 min-w-0">
    <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
      done ? 'bg-sage text-white' : active ? 'bg-pierian text-white' : 'bg-gray-200 text-gray-500'
    }`}>
      {done ? <Check size={14}/> : num}
    </div>
    <span className={`text-xs font-medium truncate ${active ? 'text-pierian' : done ? 'text-gray-700' : 'text-gray-400'}`}>{label}</span>
  </div>
);

const ScoreBar = ({ value, label }: { value: number; label: string }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] text-gray-600 font-medium">{label}</span>
      <span className="text-[11px] font-bold text-gray-800">{value.toFixed(1)}/10</span>
    </div>
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${value >= 8 ? 'bg-emerald-500' : value >= 6 ? 'bg-blue-500' : value >= 4 ? 'bg-amber-500' : 'bg-rose-400'}`}
        style={{ width: `${Math.min(100, value * 10)}%` }}
      />
    </div>
  </div>
);

// ── Main wizard component ───────────────────────────────────
const STEPS = ['Company', 'Source', 'Decision Maker', 'Delivery Fit', 'Deal Economics', 'Review'];

export const ICPQualifierWizard: React.FC<ICPWizardProps> = ({
  onClose, onComplete,
  industries = [], geographies = [], leadSources = [],
}) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<Lead>({
    id: Date.now(),
    companyName: '', industry: '', revenue: 0, employees: 0, geography: '',
    contactName: '', contactTitle: '', contactEmail: '',
    companyWebsite: '',
    dmAccess: '', dmRole: '',
    pastDelivery: '', skillAvailable: '', techCapability: '',
    locationFeasibility: '', locationSetup: '',
    dealACV: '', dealTCV: '', timeline: '',
    leadSource: '',
    notes: '',
    createdAt: new Date(), status: 'open',
  });
  const update = (patch: Partial<Lead>) => setData(prev => ({ ...prev, ...patch }));

  // Live score ─────────────────────────────────────────────
  const config = useMemo(() => createDefaultConfig(), []);
  const score = useMemo(() => calculateCompositeScore(data, config), [data, config]);

  // Leadsource options: prefer CRM-provided list, fallback to module defaults
  const leadSourceOptions: string[] = leadSources.length > 0
    ? leadSources.filter(s => s.isActive !== false).map(s => s.name)
    : [...FIELD_OPTIONS.leadSource];

  // Step validation ───────────────────────────────────────
  const canAdvance = (): boolean => {
    if (step === 0) return !!(data.companyName && data.contactName && data.contactEmail && data.industry);
    if (step === 1) return !!data.leadSource;
    if (step === 2) return !!(data.dmAccess && data.dmRole);
    if (step === 3) return !!(data.pastDelivery && data.skillAvailable && data.techCapability && data.locationFeasibility);
    if (step === 4) return !!(data.timeline);
    return true;
  };

  const next = () => { if (step < STEPS.length - 1 && canAdvance()) setStep(step + 1); };
  const back = () => { if (step > 0) setStep(step - 1); };

  const handleComplete = () => {
    // Build the ICP summary block that will be prefilled into the lead's comments
    const tierInfo = tierColors[score.tier];
    const summary = [
      `━━ ICP QUALIFICATION ━━`,
      `Tier: ${tierInfo.label} (${score.tier})`,
      `Composite Score: ${score.composite.toFixed(1)}/10`,
      `  • Readiness: ${score.readiness.toFixed(1)}`,
      `  • Deal Economics: ${score.deal.toFixed(1)}`,
      `  • Lead Source: ${score.source.toFixed(1)}`,
      ``,
      `Readiness detail:`,
      `  DM Access: ${data.dmAccess || '-'}`,
      `  DM Role: ${data.dmRole || '-'}`,
      `  Past Delivery: ${data.pastDelivery || '-'}`,
      `  Skills: ${data.skillAvailable || '-'} | Tech: ${data.techCapability || '-'}`,
      `  Location: ${data.locationFeasibility || '-'} | Setup: ${data.locationSetup || '-'}`,
      ``,
      `Deal Economics:`,
      `  ACV: ${data.dealACV ? `$${Number(data.dealACV).toLocaleString()}` : '-'}`,
      `  TCV: ${data.dealTCV ? `$${Number(data.dealTCV).toLocaleString()}` : '-'}`,
      `  Timeline: ${data.timeline || '-'}`,
      `━━━━━━━━━━━━━━━━━━━━━━`,
    ].join('\n');

    onComplete({
      companyName: data.companyName,
      contactPerson: data.contactName || '',
      email: data.contactEmail || '',
      designation: data.contactTitle || '',
      geography: data.geography,
      industry: data.industry,
      leadSource: data.leadSource,
      estimatedRevenue: String(data.dealTCV || ''),
      budget: String(data.dealACV || ''),
      comments: summary + (data.notes ? `\n\nNotes:\n${data.notes}` : ''),
      painPoints: '',
      priority: tierPriority(score.tier),
      scores: score,
    });
  };

  const tierInfo = tierColors[score.tier];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-pierian to-scarlet px-5 py-3 flex items-center justify-between text-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={18} />
            <div>
              <h2 className="text-base font-bold leading-tight">Qualify New Lead — ICP Check</h2>
              <p className="text-[11px] opacity-90">Quick 5-step qualification before adding to the CRM</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/20 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Progress pills */}
        <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            {STEPS.map((label, i) => (
              <StepPill key={label} num={i + 1} label={label} active={i === step} done={i < step} />
            ))}
          </div>
        </div>

        {/* Body: form on left, live score panel on right */}
        <div className="flex-1 overflow-hidden flex">
          {/* Form */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Step 0: Company */}
            {step === 0 && (
              <div className="space-y-4 max-w-2xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">Company & Contact</h3>
                  <p className="text-xs text-gray-500">Basic details about the prospect</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <FieldLabel>Company Name *</FieldLabel>
                    <input type="text" value={data.companyName} onChange={e => update({ companyName: e.target.value })}
                      placeholder="e.g. Tata Steel" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                  </div>
                  <div>
                    <FieldLabel>Industry *</FieldLabel>
                    <select value={data.industry} onChange={e => update({ industry: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none">
                      <option value="">Select industry</option>
                      {industries.filter(i => i.isActive !== false).map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <FieldLabel>Geography</FieldLabel>
                    <select value={data.geography} onChange={e => update({ geography: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none">
                      <option value="">Select geography</option>
                      {geographies.filter(g => g.isActive !== false).map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Contact Person *</FieldLabel>
                    <input type="text" value={data.contactName || ''} onChange={e => update({ contactName: e.target.value })}
                      placeholder="e.g. John Doe" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                  </div>
                  <div>
                    <FieldLabel>Designation</FieldLabel>
                    <input type="text" value={data.contactTitle || ''} onChange={e => update({ contactTitle: e.target.value })}
                      placeholder="e.g. VP Finance" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                  </div>
                  <div className="col-span-2">
                    <FieldLabel>Contact Email *</FieldLabel>
                    <input type="email" value={data.contactEmail || ''} onChange={e => update({ contactEmail: e.target.value })}
                      placeholder="e.g. john@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Lead Source */}
            {step === 1 && (
              <div className="space-y-4 max-w-2xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">How did this lead reach us?</h3>
                  <p className="text-xs text-gray-500">Source quality influences our priority scoring</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {leadSourceOptions.map(src => (
                    <ChoiceButton key={src} active={data.leadSource === src} onClick={() => update({ leadSource: src as LeadSource })}>
                      {src}
                    </ChoiceButton>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Decision Maker */}
            {step === 2 && (
              <div className="space-y-4 max-w-2xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2"><Users size={16}/> Decision Maker</h3>
                  <p className="text-xs text-gray-500">Access to the economic buyer is the single biggest deal predictor</p>
                </div>
                <div>
                  <FieldLabel>Do we have access to the Decision Maker?</FieldLabel>
                  <YPNButtons value={data.dmAccess} onChange={v => update({ dmAccess: v })} />
                </div>
                <div>
                  <FieldLabel>Who is the Decision Maker?</FieldLabel>
                  <div className="grid grid-cols-4 gap-2">
                    {FIELD_OPTIONS.dmRole.map(role => (
                      <ChoiceButton key={role} active={data.dmRole === role} onClick={() => update({ dmRole: role as DMRole })}>
                        {role}
                      </ChoiceButton>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Delivery Fit */}
            {step === 3 && (
              <div className="space-y-4 max-w-2xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2"><Target size={16}/> Delivery Fit</h3>
                  <p className="text-xs text-gray-500">Can we actually deliver what they need?</p>
                </div>
                <div>
                  <FieldLabel>Have we delivered this service before?</FieldLabel>
                  <YPNButtons value={data.pastDelivery} onChange={v => update({ pastDelivery: v })} />
                </div>
                <div>
                  <FieldLabel>Do we have the skills available?</FieldLabel>
                  <YPNButtons value={data.skillAvailable} onChange={v => update({ skillAvailable: v })} />
                </div>
                <div>
                  <FieldLabel>Do we have the technology capability?</FieldLabel>
                  <YPNButtons value={data.techCapability} onChange={v => update({ techCapability: v })} />
                </div>
                <div>
                  <FieldLabel><span className="inline-flex items-center gap-1"><MapPin size={12}/> Do we operate in their location?</span></FieldLabel>
                  <YPNButtons value={data.locationFeasibility} onChange={v => update({ locationFeasibility: v })} />
                </div>
                <div>
                  <FieldLabel>Can we set up delivery for this location?</FieldLabel>
                  <YPNButtons value={data.locationSetup} onChange={v => update({ locationSetup: v })} />
                </div>
              </div>
            )}

            {/* Step 4: Deal Economics */}
            {step === 4 && (
              <div className="space-y-4 max-w-2xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2"><IndianRupee size={16}/> Deal Economics</h3>
                  <p className="text-xs text-gray-500">Size and urgency of the opportunity</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel hint="Annual Contract Value in INR">ACV (INR)</FieldLabel>
                    <input type="number" value={data.dealACV || ''} onChange={e => update({ dealACV: e.target.value })}
                      placeholder="e.g. 500000" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                    <p className="text-[10px] text-gray-400 mt-1">≥₹2M=10 · ₹1-2M=8 · ₹500K-1M=6 · &lt;₹500K=3</p>
                  </div>
                  <div>
                    <FieldLabel hint="Total Contract Value in INR">TCV (INR)</FieldLabel>
                    <input type="number" value={data.dealTCV || ''} onChange={e => update({ dealTCV: e.target.value })}
                      placeholder="e.g. 2000000" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                    <p className="text-[10px] text-gray-400 mt-1">≥₹5M=10 · ₹2-5M=8 · ₹1-2M=6 · &lt;₹1M=3</p>
                  </div>
                </div>
                <div>
                  <FieldLabel>Expected Timeline to Close *</FieldLabel>
                  <div className="grid grid-cols-4 gap-2">
                    {FIELD_OPTIONS.timeline.map(t => (
                      <ChoiceButton key={t} active={data.timeline === t} onClick={() => update({ timeline: t as Timeline })}>
                        {t}
                      </ChoiceButton>
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Notes (optional)</FieldLabel>
                  <textarea value={data.notes || ''} onChange={e => update({ notes: e.target.value })}
                    rows={3} placeholder="Any extra context you want to capture..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-pierian/30 focus:border-pierian outline-none" />
                </div>
              </div>
            )}

            {/* Step 5: Review */}
            {step === 5 && (
              <div className="space-y-4 max-w-3xl">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">Qualification Summary</h3>
                  <p className="text-xs text-gray-500">Review the score and confirm to add to the CRM</p>
                </div>

                {/* Big tier banner */}
                <div className={`${tierInfo.bg} ${tierInfo.ring} ring-2 rounded-lg p-5`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-widest ${tierInfo.text} opacity-80`}>Tier {score.tier}</div>
                      <div className={`text-3xl font-black ${tierInfo.text} leading-tight`}>{tierInfo.label}</div>
                      <div className={`text-xs ${tierInfo.text} opacity-80 mt-0.5`}>{tierInfo.desc}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-[10px] font-semibold uppercase tracking-widest ${tierInfo.text} opacity-80`}>Composite</div>
                      <div className={`text-5xl font-black ${tierInfo.text} leading-none`}>{score.composite.toFixed(1)}</div>
                      <div className={`text-[10px] ${tierInfo.text} opacity-80`}>/ 10.0</div>
                    </div>
                  </div>
                </div>

                {/* Dimension breakdown */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Readiness (50%)</div>
                    <div className="text-xl font-bold text-gray-900">{score.readiness.toFixed(1)}<span className="text-xs text-gray-400">/10</span></div>
                    <ScoreBar value={score.readiness} label="" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Deal Economics (35%)</div>
                    <div className="text-xl font-bold text-gray-900">{score.deal.toFixed(1)}<span className="text-xs text-gray-400">/10</span></div>
                    <ScoreBar value={score.deal} label="" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Lead Source (15%)</div>
                    <div className="text-xl font-bold text-gray-900">{score.source.toFixed(1)}<span className="text-xs text-gray-400">/10</span></div>
                    <ScoreBar value={score.source} label="" />
                  </div>
                </div>

                {/* Captured data preview */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 space-y-1">
                  <div><span className="font-semibold">Company:</span> {data.companyName} · {data.industry}</div>
                  <div><span className="font-semibold">Contact:</span> {data.contactName} ({data.contactTitle || 'no title'}) · {data.contactEmail}</div>
                  <div><span className="font-semibold">Source:</span> {data.leadSource}</div>
                  <div><span className="font-semibold">DM:</span> {data.dmAccess} access · {data.dmRole}</div>
                  <div><span className="font-semibold">Deal:</span> ACV ${Number(data.dealACV || 0).toLocaleString()} · TCV ${Number(data.dealTCV || 0).toLocaleString()} · {data.timeline}</div>
                </div>

                {score.tier === '4' && (
                  <div className="bg-rose-50 border border-rose-200 rounded-md p-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-rose-800">
                      <strong>This lead is below our qualification threshold.</strong> Consider whether to add it at all — you can still proceed,
                      but it will be flagged for deprioritization.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live score sidebar */}
          <div className="w-60 bg-gray-50 border-l border-gray-200 p-4 flex-shrink-0 hidden lg:flex flex-col gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Live Score</div>
              <div className={`text-4xl font-black ${tierInfo.text}`}>{score.composite.toFixed(1)}</div>
              <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${tierInfo.bg} ${tierInfo.text}`}>
                {tierInfo.label}
              </div>
            </div>
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <ScoreBar value={score.readiness} label="Readiness" />
              <ScoreBar value={score.deal}      label="Deal Economics" />
              <ScoreBar value={score.source}    label="Lead Source" />
            </div>
            <div className="mt-auto text-[10px] text-gray-400 leading-snug">
              Score updates as you complete each step. Final score locks on "Add to CRM".
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
          <button
            onClick={step === 0 ? onClose : back}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1"
          >
            <ChevronLeft size={14}/>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          <div className="text-xs text-gray-400">Step {step + 1} of {STEPS.length}</div>
          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              disabled={!canAdvance()}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1 transition-colors ${
                canAdvance() ? 'bg-pierian text-white hover:bg-scarlet' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next <ChevronRight size={14}/>
            </button>
          ) : (
            <button
              onClick={handleComplete}
              className="px-4 py-1.5 rounded-md text-sm font-semibold bg-pierian text-white hover:bg-scarlet flex items-center gap-1"
            >
              <Check size={14}/> Add to CRM
            </button>
          )}
        </div>

        {/* Attribution */}
        <div className="px-5 py-1.5 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <p className="text-[10px] text-gray-400 text-center italic">
            ICP Lead Scoring tool developed by Hrishikesh
          </p>
        </div>
      </div>
    </div>
  );
};

export default ICPQualifierWizard;
